import { ClientDocumentDto } from "./client.types";

export type InvoiceStatus = "draft" | "pending" | "paid" | "overdue";

// export interface CreateInvoiceItemDto {
//   item_id?: number;
//   description: string;
//   quantity: number;
//   unit_price: number;
//   unit_type?: string;
//   tax_rate_id?: number;
//   sort_order?: number;
// }

export interface CreateInvoiceItemDto {
  item_id?: number;
  item_code: string;
  item_name: string;
  sacCode: string;
  sac_gst_rate: number;
  taxName:string;
  description: string;
  quantity: number;
  unit_price: number;
  unit_type?: string;
  tax_rate_id?: number;
  sort_order?: number;
  discount?: number;
}

export interface InvoiceDocumentDto {
  id?: number;
  file_path: string;
  file_name?: string;
  file_type?: string;
  file_size?: number;
  upload_date?: string;
  base64Content?: string;
}

export interface CreateInvoiceDto {
  client_id: number;
  clientName: string;
  issue_date: Date;
  due_date: Date;
  currency_id: number;
  currency_code: string;
  exchange_rate_id?: number;
  industry_id?: number;
  tax_inclusive?: boolean;
  payment_term_id?:number;
  payment_terms?: number;
  notes?: string;
  terms_conditions?: string;
  template_id?: number;
  is_recurring?: boolean;
  parent_invoice_id?: number;
  recurrence_pattern?: string;
  next_recurrence_date?: Date;
  status: string;
  business_type?: string;
  business_type_id?:number;
  invoice_items: CreateInvoiceItemDto[];
  emailTo?: string[];
  emailCc?:string[];
  emailMessage?: string;
  emailSubject?: string;
  natureOfPayment?: string | null;
  threashold?: number | null;
  gstType?: string | null;
  yearOfGst?: number | null;
  gstRate?: number | null;
  documents?: InvoiceDocumentDto[];
  withPaymentLink?:boolean;
  invoiceNumber:string;
  round_off?:number;
  choosedTemplateId?: number;
}
export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  lineTotal: number;
}

interface TaxSummary {
  tax_name: string;
  tax_rate: number;
  taxable_amount: string;
  tax_amount: string;
}

export interface Invoice {
  invoice_id: number;
  client_id: number;
  client_name: string;
  invoice_number: string;
  sequence_id: number;
  raw_number: number;
  status: InvoiceStatusEnum;
  issue_date: string;
  due_date: string;
  currency_id: number;
  currency_code: string;
  currency_symbol: string;
  exchange_rate_id: number | null;
  exchange_rate: number;
  industry_id: number;
  industry_name: string;
  tax_inclusive: boolean;
  subtotal: number;
  tax_total: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  payment_terms: string;
  notes: string | null;
  terms_conditions: string | null;
  template_id: number;
  template_name: string;
  is_recurring: boolean;
  parent_invoice_id: number | null;
  recurrence_pattern: string | null;
  next_recurrence_date: string | null;
  createdAt: string;
  updatedAt: string;
  invoice_items: any[]; // Replace `any` with a proper type if known
  tax_summaries: TaxSummary[];
  last_payment_date:string | null;
}

export interface TaxRate {
  id: string;
  country: string;
  industry?: string;
  name: string;
  rate: number;
  effectiveFrom?: Date;
  effectiveTo?: Date;
  description?: string;
}

export interface GetInvoiceDto {
  round_off?:number;
  invoice_id: number;
  client_id: number;
  client_name: string;
  invoice_number: string;
  sequence_id?: number;
  raw_number?: number;
  status: InvoiceStatusEnum;
  issue_date: Date;
  due_date: Date;
  currency_id: number;
  currency_code: string;
  currency_symbol: string;
  exchange_rate_id?: number;
  exchange_rate?: number;
  industry_id?: number;
  industry_name?: string;
  tax_inclusive: boolean;
  subtotal: number;
  tax_total: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  payment_terms?: string;
  notes?: string;
  terms_conditions?: string;
  template_id?: number;
  template_name?: string;
  is_recurring: boolean;
  parent_invoice_id?: number;
  recurrence_pattern?: string;
  next_recurrence_date?: Date;
  gstRate?:number;
  gstType?:string;
  createdAt: Date;
  updatedAt: Date;
  choosedTemplateId?: number;
  documents?:ClientDocumentDto[];
  invoice_items: GetInvoiceItemDto[];
  tax_summaries?: {
    tax_name: string;
    tax_rate: number;
    taxable_amount: number;
    tax_amount: number;
  }[];
}

export interface GetInvoiceItemDto {
  invoice_item_id: number;
  invoice_id: number;
  item_id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  unit_type?: string;
  line_total: number;
  tax_rate_id?: number;
  tax_name?: string;
  tax_rate?: number;
  tax_amount: number;
  sort_order: number;
  sac_gst_rate?: number;
  taxName?:string;
  discount?:number;
}

export enum InvoiceStatusEnum {
  DRAFT = "draft",
  SENT = "sent",
  VIEWED = "viewed",
  PARTIAL = "partial",
  PAID = "paid",
  OVERDUE = "overdue",
  CANCELLED = "cancelled",
  REFUNDED = "refunded",
  ACTIVE = "active",
  PENDING = "pending",
  WRITE_OFF = "write_off"
}

export interface RecordPaymentDto {
  amount: number;
  payment_date: Date;
  payment_method: string;
  payment_reference?: string;
  notes?: string;
}

// Type definitions for the invoice payment schedule

export type PaymentStatus = "paid" | "pending" | "overdue";

export interface Payment {
  amount: number;
  status: PaymentStatus;
}

export interface CalendarDay {
  day: number;
  payments: Payment[];
}

export interface UpcomingPayment {
  date: string;
  client: string;
  amount: number;
}

export interface TimelineDataPoint {
  month: string;
  value: number;
}

export interface PaymentSummaryProps {
  totalInvoiced: number;
  invoiceCount: number;
  totalPaid: number;
  paidInvoiceCount: number;
  upcomingPayments: number;
  nextPaymentDays: number;
}

export interface PaymentCalendarProps {
  month: string;
  year: number;
  calendarData: CalendarDay[];
  today: number;
}

export interface UpcomingPaymentsProps {
  payments: UpcomingPayment[];
}

export interface PaymentTimelineProps {
  chartData: TimelineDataPoint[];
  today: number;
}

export interface FinancialProjectionsProps {
  monthly: number;
  quarterly: number;
  annual: number;
  reliability: number;
}

export interface GetCalendarDataProps {
  startDate: string;
  endDate: string;
}

export interface GetPaymentScheduleData {
  startDate?: string;
  endDate?: string;
}

export interface GetAgendaViewDataProps extends GetCalendarDataProps {
  page?: number;
  pageSize?: number;
  sortBy?:string;
  sortProperty?:string;
}

export interface CalculateInvoiceResultDto {
  total: number;
  subTotal: number;
  sacTotal: number;
  taxTotal: number;
  totalDiscount:number;
  roundOff?:number;
}

